import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import WizardProgress from '../site-setup-wizard/components/WizardProgress';
import { setupService } from '../../services/setupService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { sanitizeError } from '../../utils/sanitizeError';

const SetupFlowCreateSite = () => {
  const navigate = useNavigate();
  const { userProfile, user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [error, setError] = useState('');
  // Plan limit bypass state
  const [limitReached, setLimitReached] = useState(false);
  const [existingSite, setExistingSite] = useState(null);
  const [useExisting, setUseExisting] = useState(false);
  // Checkout return state
  const [checkoutVerified, setCheckoutVerified] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [webhookReady, setWebhookReady] = useState(false);

  const steps = [
    { id: 1, title: 'Company Details' },
    { id: 2, title: 'Choose Plan' },
    { id: 3, title: 'Create First Site' },
    { id: 4, title: 'Invite Team' },
    { id: 5, title: 'Daily Site Checklist' }
  ];

  useEffect(() => {
    // Wait for auth to be ready before doing anything
    if (authLoading) return;

    // Handle Stripe checkout return
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params?.get('checkout');
    if (checkoutStatus === 'success') {
      setCheckoutVerified(true);
      // Clean URL without reload
      window.history?.replaceState({}, '', '/setup-flow-create-site');

      // Actively verify session — don't rely solely on AuthContext user
      // which may not be hydrated yet when this effect fires.
      (async () => {
        let sessionUser = user;
        if (!sessionUser) {
          // Try to get the session directly from Supabase
          const { data: { session } } = await supabase?.auth?.getSession();
          sessionUser = session?.user ?? null;
        }

        // DEBUG: Log auth user ID after checkout success return
        console.log('[DEBUG][CHECKOUT_RETURN] setup-flow-create-site. user.id=', sessionUser?.id, 'session present=', !!sessionUser);

        if (!sessionUser) {
          // Session was genuinely lost during Stripe redirect (e.g. mobile Safari).
          // Redirect to login with a return_to param so the user can sign in
          // and be sent back here to complete onboarding.
          console.warn('[DEBUG][CHECKOUT_RETURN] No session after checkout. Redirecting to login.');
          navigate('/login?return_to=/setup-flow-create-site', { replace: true });
          return;
        }

        // Load company immediately, then re-load after 4s to catch webhook-written subscription fields
        loadCompanyData();
        setTimeout(() => {
          loadCompanyData();
          setWebhookReady(true);
        }, 4000);
      })();
    } else if (checkoutStatus === 'cancelled') {
      navigate('/setup-flow-plan-confirmation?checkout=cancelled');
      return;
    } else {
      if (user) loadCompanyData();
    }
  }, [user, authLoading]);

  const loadCompanyData = async () => {
    try {
      const { data, error } = await companyService?.getUserCompany();
      if (error) throw error;

      if (data) {
        setCompanyId(data?.id);
        if (data?.billing_interval) setBillingInterval(data?.billing_interval);

        // Check if plan limit is already reached
        const { data: canAdd } = await companyService?.canAddActiveSite(data?.id);
        if (!canAdd) {
          setLimitReached(true);
          // Try to find an existing active site to reuse
          const { data: site } = await setupService?.getExistingSiteForOnboarding(data?.id);
          setExistingSite(site);
          if (site) setUseExisting(true);
        }

        const savedProgress = setupService?.loadProgress();
        if (savedProgress?.formData?.siteName) {
          setSiteName(savedProgress?.formData?.siteName);
        }
        if (savedProgress?.formData?.siteAddress) {
          setSiteAddress(savedProgress?.formData?.siteAddress);
        }
      }
    } catch (err) {
      console.error('Failed to load company:', err);
      setError('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/setup-flow-plan-confirmation');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // If using an existing site, just save progress and continue
    if (useExisting && existingSite) {
      setupService?.saveProgress(3, {
        siteName: existingSite?.site_name,
        siteAddress: '',
        siteId: existingSite?.id
      });
      navigate('/setup-flow-invite-team');
      return;
    }

    if (!siteName?.trim()) {
      setError('Site name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      let result;

      if (limitReached) {
        // Bypass plan limit for onboarding — insert directly without limit check
        result = await setupService?.createFirstSiteForOnboarding(companyId, siteName, siteAddress);
      } else {
        result = await setupService?.createFirstSite(companyId, siteName, siteAddress);
      }

      const { data, error } = result || {};

      if (error) {
        throw error;
      }

      setupService?.saveProgress(3, { siteName, siteAddress, siteId: data?.id });
      navigate('/setup-flow-invite-team');
    } catch (err) {
      setError(sanitizeError(err?.message, 'Failed to create site'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Create Your First Site</h1>
          <p className="text-muted-foreground">Set up your first active construction site</p>
        </div>

        <WizardProgress currentStep={3} totalSteps={5} steps={steps} onExit={() => navigate('/')} />

        {checkoutVerified && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
            <span className="text-success text-xl">✓</span>
            <div>
              <p className="text-sm font-semibold text-success">Payment successful — your subscription is now active!</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {webhookReady ? 'Your plan and billing details have been updated.' : 'Confirming your subscription details…'}
              </p>
            </div>
          </div>
        )}

        <div className="bg-card rounded-lg shadow-elevation-1 p-6 md:p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Site Details</h2>


            {/* Plan limit reached banner */}
            {limitReached && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-amber-500 text-lg mt-0.5">⚠️</span>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Active site limit reached</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Your plan's active site limit is full. You can use an existing site to continue
                      setup, or create a new site to complete onboarding.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Existing site option when limit is reached */}
            {limitReached && existingSite && (
              <div className="space-y-3">
                <div
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    useExisting
                      ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                  }`}
                  onClick={() => setUseExisting(true)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        useExisting ? 'border-accent bg-accent' : 'border-muted-foreground'
                      }`}
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground">Use existing site</p>
                      <p className="text-sm text-muted-foreground">{existingSite?.site_name}</p>
                    </div>
                  </div>
                </div>

                <div
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    !useExisting
                      ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                  }`}
                  onClick={() => setUseExisting(false)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        !useExisting ? 'border-accent bg-accent' : 'border-muted-foreground'
                      }`}
                    />
                    <p className="text-sm font-medium text-foreground">Create a new site anyway</p>
                  </div>
                </div>
              </div>
            )}

            {/* Show form only when creating new site */}
            {!useExisting && (
              <>
                <Input
                  label="Site Name"
                  name="siteName"
                  value={siteName}
                  onChange={(e) => { setSiteName(e?.target?.value); setError(''); }}
                  error={!siteName?.trim() ? error : ''}
                  required
                  placeholder="e.g., Riverside Development, Unit 4 Warehouse"
                  description="You can update this later"
                />

                <Input
                  label="Address / Postcode (optional)"
                  name="siteAddress"
                  value={siteAddress}
                  onChange={(e) => setSiteAddress(e?.target?.value)}
                  placeholder="e.g., 12 High Street, London, SW1A 1AA"
                  description="Optional — you can add the full address later"
                />
              </>
            )}

            {error && (siteName?.trim() || useExisting) && (
              <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="flex justify-between items-center pt-4">
              <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
                Back
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || (!useExisting && !siteName?.trim())}
                size="lg"
              >
                {useExisting ? 'Use This Site & Continue' : 'Create Site & Continue'}
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Step 3 of 5 — Your site will be created as Active
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupFlowCreateSite;